import { Injectable } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { API, ClienteApiConLimite, parametros, traducirErrorBodega } from './api-bodega-comun';
import { ErrorBodega } from '../models/bodega.model';
import { AvisoPortal, ConteoAvisos, FiltrosAvisos, PaginaAvisos, normalizarAviso } from '../models/aviso-portal.model';

const MSJ_LISTA = 'No se pudieron cargar tus notificaciones. Revisa tu conexión e inténtalo de nuevo.';
const MSJ_CONTEO = 'No se pudo consultar cuántas notificaciones tienes sin leer.';
const MSJ_LEER = 'No se pudo marcar la notificación como leída. Revisa tu conexión e inténtalo de nuevo.';
const MSJ_ARCHIVAR = 'No se pudo archivar la notificación. Revisa tu conexión e inténtalo de nuevo.';
const MSJ_LEER_TODAS = 'No se pudieron marcar las notificaciones como leídas. Revisa tu conexión e inténtalo de nuevo.';
const MSJ_YA_NO_ESTA = 'Esa notificación ya no está disponible. Actualiza la lista para ver las que tienes.';

/**
 * Como `traducirErrorBodega`, con un texto propio para el 404 (el de Bodega habla de obras): un aviso ajeno o que ya se borró (la retención) responde 404.
 * Un `CURSOR_INVALIDO` llega con su `code`: la pantalla vuelve a pedir desde el principio.
 */
export function traducirErrorDeAvisos(err: unknown, porDefecto: string): ErrorBodega {
  if (err instanceof HttpErrorResponse && err.status === 404) return new ErrorBodega(404, MSJ_YA_NO_ESTA, false, 'AVISO_NO_ENCONTRADO');
  return traducirErrorBodega(err, porDefecto);
}

/**
 * Cliente HTTP de los avisos de la campana (`/api/avisos`, contrato de la Etapa 11 parte B). Hereda de `ClienteApiConLimite` el cupo de 26 peticiones por minuto y endpoint (el servidor deja 30) y el reintento acotado
 * tras un 429. Todas las rutas son idempotentes, así que todas se pueden reintentar. Cada persona ve y cambia solo lo suyo: no hay detalle por id.
 */
@Injectable({ providedIn: 'root' })
export class AvisosApiService extends ClienteApiConLimite {
  private readonly raiz = `${API}/avisos`;

  async listar(f: FiltrosAvisos = {}, senal?: AbortSignal): Promise<PaginaAvisos> {
    const params = parametros({ estado: f.estado, tipo: f.tipo, limite: f.limite, cursor: f.cursor });
    const bruto = await this.pedir<unknown>('GET avisos lista', () => this.http.get<unknown>(this.raiz, { params }), true, MSJ_LISTA, traducirErrorDeAvisos, senal);
    const b = bruto as { items?: unknown; siguiente?: unknown; limite?: unknown } | null;
    const items = Array.isArray(b?.items) ? b.items.map(normalizarAviso).filter((a): a is AvisoPortal => a !== null) : [];
    return {
      items,
      siguiente: typeof b?.siguiente === 'string' && b.siguiente ? b.siguiente : null,
      limite: typeof b?.limite === 'number' ? b.limite : items.length,
    };
  }

  async conteo(): Promise<ConteoAvisos> {
    const bruto = await this.pedir<unknown>('GET avisos conteo', () => this.http.get<unknown>(`${this.raiz}/conteo`), true, MSJ_CONTEO, traducirErrorDeAvisos);
    const b = bruto as { noLeidas?: unknown; hayUrgentes?: unknown } | null;
    const n = Number(b?.noLeidas);
    return { noLeidas: Number.isFinite(n) && n > 0 ? Math.floor(n) : 0, hayUrgentes: b?.hayUrgentes === true };
  }

  async leer(id: number): Promise<AvisoPortal | null> {
    return normalizarAviso(await this.pedir<unknown>('PATCH avisos leer', () => this.http.patch<unknown>(`${this.raiz}/${id}/leer`, {}), true, MSJ_LEER, traducirErrorDeAvisos));
  }

  async archivar(id: number): Promise<AvisoPortal | null> {
    return normalizarAviso(await this.pedir<unknown>('PATCH avisos archivar', () => this.http.patch<unknown>(`${this.raiz}/${id}/archivar`, {}), true, MSJ_ARCHIVAR, traducirErrorDeAvisos));
  }

  /** Marca como leídos los avisos activos (de un tipo, si se pide); devuelve cuántos cambiaron. */
  async leerTodas(tipo?: string): Promise<number> {
    const cuerpo = tipo ? { tipo } : {};
    const bruto = await this.pedir<unknown>('POST avisos leer-todas', () => this.http.post<unknown>(`${this.raiz}/leer-todas`, cuerpo), true, MSJ_LEER_TODAS, traducirErrorDeAvisos);
    const n = Number((bruto as { actualizadas?: unknown } | null)?.actualizadas);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  }
}
